import StyledButton from '@/components/styled/StyledButton';
import StyledContainer from '@/components/styled/StyledContainer';
import StyledListHeader from '@/components/styled/StyledListHeader';
import StyledNextPreviousButtons from '@/components/styled/StyledNextPreviousButtons';
import StyledTable from '@/components/styled/StyledTable';
import Modal from '@/components/ui/Modal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PAGE_SIZE } from '@/constants/pageSize';
import { useToast } from '@/hooks/useToast';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import {
  useGetTrashedBenefits,
  useGetTrashedCircuits,
  useRestoreBenefits,
  useRestoreCircuits,
} from '@/hooks/useTrash';
import { useTranslation } from '@/i18n';
import {
  SkippedTrashLink,
  TRASHED_BENEFIT_COLUMNS,
  TRASHED_CIRCUIT_COLUMNS,
} from '@/types/trash/trashColumnDef';
import { formatBenefitCode, formatCircuitCode } from '@/utils/trash/formatCode';
import { RowSelectionState } from '@tanstack/react-table';
import { ArchiveRestore } from 'lucide-react';
import { useState } from 'react';

const formatLabel = (code: string, name: string | null) =>
  [code, name].filter(Boolean).join(' ');

const RestoreSelectionButton = ({
  count,
  onClick,
}: {
  count: number;
  onClick: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <StyledButton
      title={t('trash.restoreSelected', { count })}
      className="font-semibold gap-1 px-3 py-2"
      onClick={onClick}
      icon={<ArchiveRestore className="size-5 stroke-brand-600" />}
      variant="outlined"
    />
  );
};

const TrashedBenefitsTab = () => {
  const { t } = useTranslation();
  const { toastAsync } = useToast();
  const getTranslatedConst = useTranslatedConst();
  const [page, setPage] = useState(1);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [skippedLinks, setSkippedLinks] = useState<SkippedTrashLink[]>([]);

  const { data, isSuccess } = useGetTrashedBenefits({
    page,
    pageSize: PAGE_SIZE,
  });
  const { mutateAsync: restoreBenefits } = useRestoreBenefits();

  const selectedIds = Object.keys(rowSelection);

  const restore = (benefitIds: string[]) =>
    toastAsync(restoreBenefits({ benefitIds }), {
      pending: t('trash.restoring'),
      success: t('trash.restored.benefits', { count: benefitIds.length }),
    }).then((result) => {
      setRowSelection({});
      setSkippedLinks(result.skippedLinks);
    });

  return (
    <div className="flex flex-col gap-6 pt-4">
      {selectedIds.length > 0 && (
        <div className="flex justify-end">
          <RestoreSelectionButton
            count={selectedIds.length}
            onClick={() => restore(selectedIds)}
          />
        </div>
      )}
      <StyledTable
        columns={getTranslatedConst(TRASHED_BENEFIT_COLUMNS)}
        data={data?.benefits ?? []}
        isLoading={!isSuccess}
        getRowId={(row) => row.id}
        rowSelection={rowSelection}
        setSelection={setRowSelection}
        onRestoreRow={(row) => restore([row.original.id])}
      />
      {isSuccess && (
        <StyledNextPreviousButtons
          page={page}
          pageSize={PAGE_SIZE}
          setPage={setPage}
          dataLength={data.total}
          label={t('trash.benefit')}
        />
      )}
      {skippedLinks.length > 0 && (
        <Modal
          closeModal={() => setSkippedLinks([])}
          title={t('trash.skippedLinks.title')}
          message={t('trash.skippedLinks.message')}
          actionTitle={t('common.close')}
          actionOnClick={() => setSkippedLinks([])}
          hasAction={false}
          cancelBtnLabel={t('common.close')}
        >
          <ul className="flex flex-col gap-2 text-sm text-gray-700 list-disc pl-5">
            {skippedLinks.map((link, index) => (
              <li key={`${link.benefitId}-${index}`}>
                {t(`trash.skippedLinks.reasons.${link.reason}`, {
                  benefit: formatLabel(
                    formatBenefitCode(link.benefitCode),
                    link.benefitName,
                  ),
                  circuit: formatLabel(
                    formatCircuitCode(link.circuitCode),
                    link.circuitName,
                  ),
                  hotel: formatLabel(
                    formatBenefitCode(link.hotelCode),
                    link.hotelName,
                  ),
                })}
              </li>
            ))}
          </ul>
        </Modal>
      )}
    </div>
  );
};

const TrashedCircuitsTab = () => {
  const { t } = useTranslation();
  const { toastAsync } = useToast();
  const getTranslatedConst = useTranslatedConst();
  const [page, setPage] = useState(1);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { data, isSuccess } = useGetTrashedCircuits({
    page,
    pageSize: PAGE_SIZE,
  });
  const { mutateAsync: restoreCircuits } = useRestoreCircuits();

  const selectedIds = Object.keys(rowSelection);

  const restore = (circuitIds: string[]) =>
    toastAsync(restoreCircuits({ circuitIds }), {
      pending: t('trash.restoring'),
      success: t('trash.restored.circuits', { count: circuitIds.length }),
    }).then(() => setRowSelection({}));

  return (
    <div className="flex flex-col gap-6 pt-4">
      {selectedIds.length > 0 && (
        <div className="flex justify-end">
          <RestoreSelectionButton
            count={selectedIds.length}
            onClick={() => restore(selectedIds)}
          />
        </div>
      )}
      <StyledTable
        columns={getTranslatedConst(TRASHED_CIRCUIT_COLUMNS)}
        data={data?.circuits ?? []}
        isLoading={!isSuccess}
        getRowId={(row) => row.id}
        rowSelection={rowSelection}
        setSelection={setRowSelection}
        onRestoreRow={(row) => restore([row.original.id])}
      />
      {isSuccess && (
        <StyledNextPreviousButtons
          page={page}
          pageSize={PAGE_SIZE}
          setPage={setPage}
          dataLength={data.total}
          label={t('trash.circuit')}
        />
      )}
    </div>
  );
};

const TrashPage = () => {
  const { t } = useTranslation();

  return (
    <StyledContainer>
      <StyledListHeader
        title={t('trash.title')}
        subTitle={t('trash.subtitle')}
      />
      <div className="flex flex-col w-full h-auto p-6 gap-6">
        <Tabs defaultValue="benefits" className="w-full">
          <TabsList>
            <TabsTrigger value="benefits" className="hover:cursor-pointer">
              {t('trash.tabs.benefits')}
            </TabsTrigger>
            <TabsTrigger value="circuits" className="hover:cursor-pointer">
              {t('trash.tabs.circuits')}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="benefits">
            <TrashedBenefitsTab />
          </TabsContent>
          <TabsContent value="circuits">
            <TrashedCircuitsTab />
          </TabsContent>
        </Tabs>
      </div>
    </StyledContainer>
  );
};

export default TrashPage;
